import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Package, AlertTriangle, X, Plus, Trash2 } from "lucide-react";
import { UNITS, SHIPPING_TYPES, orderItems } from "../lib/constants";
import { ProductCombobox } from "../components/ProductCombobox";
import { CustomerCombobox } from "../components/CustomerCombobox";

const makeEmpty = () => ({
  company_name: "", phone: "", delivery_date: "", shipping_type: "Ambar",
  note: "", customer_id: null,
  items: [{ product_id: null, product_code: "", product_name: "", quantity: "", unit: "Litre" }],
});

export default function OrderEntry() {
  const [form, setForm] = useState(makeEmpty);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [dup, setDup] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/products/top?limit=5").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const pickCustomerObj = (c) =>
    setForm((f) => ({ ...f, customer_id: c.id, company_name: c.company_name, phone: c.phone }));

  const pickProduct = (p) => {
    addItemFromPreset(p);
  };

  const setItem = (idx, k, v) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));

  const selectProduct = (idx, p) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, product_id: p.id, product_code: p.product_code, product_name: p.product_name, unit: p.unit || it.unit } : it)) }));

  const invalidateProduct = (idx, field, text) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, product_id: null, [field === "code" ? "product_code" : "product_name"]: text } : it)) }));

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { product_id: null, product_code: "", product_name: "", quantity: "", unit: "Litre" }] }));

  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const addItemFromPreset = (p) =>
    setForm((f) => {
      const items = [...f.items];
      const emptyIdx = items.findIndex((it) => !it.product_name && !it.product_code);
      const filled = { product_id: p.id, product_code: p.product_code, product_name: p.product_name, quantity: items[emptyIdx]?.quantity || "", unit: p.unit };
      if (emptyIdx >= 0) items[emptyIdx] = filled;
      else items.push({ ...filled, quantity: "" });
      return { ...f, items };
    });

  const actuallyCreate = async () => {
    setDup(null);
    setLoading(true);
    try {
      const items = form.items
        .filter((it) => it.product_id && it.quantity)
        .map((it) => ({ product_id: it.product_id, product_code: it.product_code, product_name: it.product_name, quantity: parseFloat(it.quantity), unit: it.unit }));
      const payload = {
        customer_id: form.customer_id, company_name: form.company_name, phone: form.phone,
        delivery_date: form.delivery_date,
        shipping_type: form.shipping_type, note: form.note, items,
      };
      const { data } = await api.post("/orders", payload);
      setLastOrder(data);
      toast.success(`Sipariş oluşturuldu: ${data.order_no}`);
      setForm(makeEmpty());
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const unselected = form.items.filter((it) => (it.product_code || it.product_name) && !it.product_id);
    if (unselected.length > 0) {
      toast.error("Lütfen ürünleri listeden seçin. Katalogda olmayan ürün kaydedilemez.");
      return;
    }
    const validItems = form.items.filter((it) => it.product_id && it.quantity);
    if (!form.company_name || !form.phone || validItems.length === 0) {
      toast.error("Firma, telefon ve en az bir seçili ürün (miktar ile) zorunludur");
      return;
    }
    setLoading(true);
    try {
      const codes = form.items.map((it) => it.product_code).filter(Boolean).join(",");
      const { data } = await api.get("/orders/check-duplicate", {
        params: { company_name: form.company_name, product_codes: codes },
      });
      if (data.duplicate) {
        setDup(data.matches);
        setLoading(false);
        return;
      }
    } catch {
      // if the check fails, don't block order creation
    }
    setLoading(false);
    await actuallyCreate();
  };

  const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-3 text-base outline-none transition-colors focus:border-primary";
  const labelCls = "mb-1.5 block text-sm font-semibold text-foreground";

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black tracking-tight">Yeni Sipariş</h1>
        <p className="text-sm text-muted-foreground">Hızlı giriş · zorunlu alanlar işaretli</p>
      </div>

      {lastOrder && (
        <div data-testid="last-order-banner" className="mb-5 flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <div className="text-sm">
            <span className="font-mono font-bold text-emerald-700">{lastOrder.order_no}</span>{" "}
            siparişi panoya düşürüldü.
          </div>
        </div>
      )}

      <form onSubmit={submit} data-testid="order-entry-form" className="space-y-5 rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
        {/* quick presets */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Müşteri / Firma *</label>
            <CustomerCombobox
              testid="order-company"
              value={form.company_name}
              selectedId={form.customer_id}
              customers={customers.filter((c) => c.is_active !== false)}
              onPick={pickCustomerObj}
              onType={(t) => setForm((f) => ({ ...f, company_name: t, customer_id: null }))}
            />
            <p className="mt-1 text-xs text-muted-foreground">Kayıtlı müşteriyi seçince telefon otomatik dolar.</p>
          </div>
          <div>
            <label className={labelCls}>Telefon *</label>
            <input data-testid="order-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0532 ..." />
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className={`${labelCls} mb-0`}>Ürünler *</label>
            <span className="text-xs text-muted-foreground">{form.items.length} kalem</span>
          </div>
          <div className="space-y-3" data-testid="order-items">
            {form.items.map((it, idx) => (
              <div key={idx} data-testid={`order-item-${idx}`} className="rounded-xl border border-border bg-secondary/30 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-foreground">Ürün {idx + 1}</span>
                  {form.items.length > 1 && (
                    <button type="button" data-testid={`remove-item-${idx}`} onClick={() => removeItem(idx)} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-500/10">
                      <Trash2 className="h-3.5 w-3.5" /> Kaldır
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ProductCombobox item={it} field="code" testid={`item-code-${idx}`} onSelect={(p) => selectProduct(idx, p)} onInvalidate={(t) => invalidateProduct(idx, "code", t)} />
                  <ProductCombobox item={it} field="name" testid={`item-name-${idx}`} onSelect={(p) => selectProduct(idx, p)} onInvalidate={(t) => invalidateProduct(idx, "name", t)} />
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <input data-testid={`item-quantity-${idx}`} type="number" step="any" placeholder="Miktar *" className={inputCls} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} />
                  <select data-testid={`item-unit-${idx}`} className={inputCls} value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)}>
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="add-item-btn" onClick={addItem} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/5">
            <Plus className="h-4 w-4" /> Ürün Ekle
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Teslim Tarihi</label>
            <input data-testid="order-delivery-date" type="date" className={inputCls} value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Sevk Şekli</label>
            <select data-testid="order-shipping" className={inputCls} value={form.shipping_type} onChange={(e) => set("shipping_type", e.target.value)}>
              {SHIPPING_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Not</label>
          <textarea data-testid="order-note" rows={2} className={inputCls} value={form.note} onChange={(e) => set("note", e.target.value)} />
        </div>

        <button
          data-testid="order-submit-btn"
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-primary py-4 text-lg font-bold text-white shadow-lg shadow-primary/30 transition-all hover:brightness-110 disabled:opacity-50"
        >
          {loading ? "Kaydediliyor..." : "Siparişi Oluştur"}
        </button>
      </form>

      {dup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="duplicate-warning-modal">
          <div className="w-full max-w-md rounded-2xl border border-amber-500/40 bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">Olası Mükerrer Sipariş</h3>
                <p className="text-sm text-muted-foreground">Son 6 saatte aynı firma ve ürün için sipariş bulundu</p>
              </div>
            </div>
            <div className="mb-4 max-h-56 space-y-2 overflow-y-auto">
              {dup.map((m) => (
                <div key={m.order_no} className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
                  <span className="font-mono font-bold text-primary">{m.order_no}</span> ·{" "}
                  {orderItems(m).map((it) => `${it.product_code} ${it.quantity}${it.unit}`).join(", ")}
                  <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString("tr-TR")}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button data-testid="dup-cancel-btn" onClick={() => setDup(null)} className="flex items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 py-3 font-semibold hover:bg-secondary">
                <X className="h-4 w-4" /> Vazgeç
              </button>
              <button data-testid="dup-force-create-btn" onClick={actuallyCreate} disabled={loading} className="flex-1 rounded-xl bg-amber-500 py-3 font-bold text-white hover:bg-amber-600 disabled:opacity-50">
                Yine de Oluştur
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
